// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Trend rendering data layer: builds a per-flight trend row from the
// analysis summary, locates the anchor flight, computes render +
// data-only signatures used to cache trend views, picks the best of
// duplicate rows when the same flight appears with multiple analysis
// runs, normalizes server rows for the table, and loads/saves the
// database-backed trend snapshot.
//
// Exposed as a factory because the cluster reads several mutable
// app.js globals (selectedFlight, detectionSensitivity, active trend
// metric/mode/sort, trendExcludedFlightIds) and a handful of helpers
// (sensor summary factory functions, selectedTrendAircraftDatabaseId,
// appReadOnlyMode) that need late binding.

import { buildTrendRow as buildTrendRowFromContext } from "../trends/trend-row-builder.js";
import { scoreClass } from "../core/score.js";
import { trendDateLabel } from "../trends/trend-row-builder.js";

export function createTrendRowComputation({
  trendCacheSchemaVersion,
  databaseTrendSnapshotEndpoint,
  trendExcludedFlightIds,
  applyTrendRowCap = (rows = []) => rows,
  quantile,
  formatPointTime,
  sensorReviewSummaries,
  sensorSummaryDisplayLabel,
  sensorSummaryEvidenceParts,
  sensorSummaryEvidenceText,
  sensorSummaryPeerMismatchCount,
  selectedTrendAircraftDatabaseId,
  appReadOnlyMode,
  getSelectedFlight,
  getDetectionSensitivity,
  getActiveTrendMetric,
  getActiveTrendEvidenceMode,
  getActiveTrendEvidenceSort,
  getActiveTrendEvidenceLayout,
}) {
  function buildTrendRow(entry, points, summary, options = {}) {
    return buildTrendRowFromContext(entry, points, summary, {
      quantile,
      formatPointTime,
      sensorReviewSummaries,
      sensorSummaryDisplayLabel,
      sensorSummaryEvidenceParts,
      sensorSummaryEvidenceText,
      sensorSummaryPeerMismatchCount,
      instrumentationSuspicion: summary.instrumentationSuspicion,
      engineDescriptors: options.engineDescriptors,
    });
  }

  function trendSelectedFlightIndex(rows) {
    const selectedFlight = getSelectedFlight();
    if (!selectedFlight?.id) return -1;
    return rows.findIndex((row) => row.id === selectedFlight.id);
  }

  function trendAnchorIndex(rows) {
    return rows.length - 1;
  }

  function trendWindowRows(rows, limit = 10) {
    if (!rows.length) return [];
    const anchorIndex = trendAnchorIndex(rows);
    if (anchorIndex < 0) return [];
    const start = Math.max(0, anchorIndex - limit + 1);
    return rows.slice(start, anchorIndex + 1);
  }

  function scoreClassForValue(value) {
    return scoreClass(value);
  }

  function rowChtVisualSignature(row) {
    const heatSeconds = (row?.chtHeatBandsByCylinder ?? [])
      .reduce((total, item) => total + (Number(item?.totalSeconds) || 0), 0);
    const distributionSamples = (row?.chtDistributionByCylinder ?? [])
      .reduce((total, item) => total + (Number(item?.samples) || 0), 0);
    return `${Math.round(heatSeconds)}:${distributionSamples}`;
  }

  function rowOilVisualSignature(row) {
    const trend = row?.oilTrend ?? {};
    return [
      trend.pressureMedian,
      trend.pressurePostStartMin,
      trend.pressurePostStartMax,
      trend.pressureMin,
      trend.pressureMax,
      trend.temperatureMedian,
    ].map((value) => Number.isFinite(value) ? Math.round(value * 10) / 10 : "").join(":");
  }

  function rowValveVisualSignature(row) {
    return [
      row?.valveScore,
      row?.valveAmplitude,
      row?.valveCylinder,
      row?.valveRatio,
      row?.valveFrequencyCpm,
      row?.valveWindowSeconds,
      row?.valveCyclesInWindow,
    ].map((value) => Number.isFinite(value) ? Math.round(value * 10) / 10 : "").join(":");
  }

  function trendRenderSignature(rows, aircraft) {
    return [
      trendCacheSchemaVersion,
      getDetectionSensitivity(),
      aircraft,
      getActiveTrendMetric(),
      getActiveTrendEvidenceMode(),
      getActiveTrendEvidenceSort(),
      getActiveTrendEvidenceLayout?.() ?? "",
      getSelectedFlight()?.id ?? "none",
      rows.map((row) => `${row.id}:${row.error ?? ""}:${row[getActiveTrendMetric()] ?? ""}:${rowChtVisualSignature(row)}:${rowOilVisualSignature(row)}:${rowValveVisualSignature(row)}`).join(","),
    ].join("|");
  }

  function trendDataRenderSignature(rows, aircraft) {
    return [
      trendCacheSchemaVersion,
      getDetectionSensitivity(),
      aircraft,
      rows.map((row) => `${row.id}:${row.error ?? ""}:${row.overallScore ?? ""}:${row.findingTitle ?? ""}:${rowChtVisualSignature(row)}:${rowOilVisualSignature(row)}:${rowValveVisualSignature(row)}`).join(","),
    ].join("|");
  }

  function trendRowsIncludedByPreference(rows = []) {
    return rows.filter((row) => row?.id && !trendExcludedFlightIds.has(row.id));
  }

  function trendRowCompletenessScore(row) {
    if (!row || typeof row !== "object") return -1;
    return [
      row.overallScore,
      row.turboScore,
      row.oilSystemScore,
      row.chtScore,
      row.egtScore,
      row.valveScore,
      row.valveAmplitude,
      row.combustionScore,
      row.divergenceScore,
      row.descentEgtDropoutScore,
      row.inductionLeakScore,
      row.magScore,
      row.carbIceScore,
      row.coolingScore,
      row.coolingRateScore,
      row.chargingScore,
      row.electricalScore,
      (row?.chtHeatBandsByCylinder ?? []).some((item) => Number(item?.totalSeconds) > 0) ? 1 : null,
      (row?.chtDistributionByCylinder ?? []).some((item) => Number(item?.samples) > 0) ? 1 : null,
    ].filter(Number.isFinite).length;
  }

  function preferTrendRowCandidate(current, candidate) {
    if (!current) return candidate;
    const currentCompleteness = trendRowCompletenessScore(current);
    const candidateCompleteness = trendRowCompletenessScore(candidate);
    if (candidateCompleteness !== currentCompleteness) return candidateCompleteness > currentCompleteness ? candidate : current;
    const currentOverall = Number.isFinite(current.overallScore) ? current.overallScore : -1;
    const candidateOverall = Number.isFinite(candidate.overallScore) ? candidate.overallScore : -1;
    if (candidateOverall !== currentOverall) return candidateOverall > currentOverall ? candidate : current;
    const currentTurbo = Number.isFinite(current.turboScore) ? current.turboScore : -1;
    const candidateTurbo = Number.isFinite(candidate.turboScore) ? candidate.turboScore : -1;
    if (candidateTurbo !== currentTurbo) return candidateTurbo > currentTurbo ? candidate : current;
    const currentRunId = String(current.databaseAnalysisRunId ?? "");
    const candidateRunId = String(candidate.databaseAnalysisRunId ?? "");
    if (candidateRunId && currentRunId && candidateRunId !== currentRunId) {
      return candidateRunId.localeCompare(currentRunId) > 0 ? candidate : current;
    }
    return current;
  }

  function normalizeTrendRowsForDisplay(rows = [], aircraft = "") {
    const bestByIdentity = new Map();
    (Array.isArray(rows) ? rows : [])
      .filter((row) => row && typeof row === "object" && row.id)
      .forEach((row) => {
        const normalized = {
          ...row,
          aircraft: row.aircraft ?? aircraft,
          title: row.title ?? row.flightTitle ?? row.id,
          monitor: row.monitor ?? "",
          dateValue: Number.isFinite(row.dateValue) ? row.dateValue : 0,
          dateLabel: row.dateLabel ?? trendDateLabel(row.dateValue ?? 0),
        };
        const dedupeKey = [normalized.id, normalized.title, normalized.dateValue].join("|");
        bestByIdentity.set(dedupeKey, preferTrendRowCandidate(bestByIdentity.get(dedupeKey), normalized));
      });
    return [...bestByIdentity.values()]
      .sort((a, b) => (a.dateValue ?? 0) - (b.dateValue ?? 0) || String(a.title ?? "").localeCompare(String(b.title ?? "")));
  }

  function databaseTrendSnapshotRows(snapshot, aircraft) {
    const snapshotJson = snapshot?.snapshot_json ?? snapshot?.snapshotJson ?? snapshot;
    const rows = snapshotJson?.trendRows ?? snapshotJson?.rows;
    if (!Array.isArray(rows) || !rows.length) return null;
    const snapshotVersion = snapshotJson?.trendRowsVersion ?? snapshotJson?.renderVersion ?? "";
    if (snapshotVersion !== trendCacheSchemaVersion) return null;
    const renderReady = snapshotJson?.renderReady === true || snapshotJson?.uiReady === true;
    if (!renderReady) return null;
    const normalizedRows = normalizeTrendRowsForDisplay(rows, aircraft);
    // Server snapshots carry the full history; cap to the most-recent set so
    // DB-mode trends honor the same 50-flight / 100-hour limit as file mode.
    const includedRows = applyTrendRowCap(trendRowsIncludedByPreference(normalizedRows));
    return includedRows.some((row) => Number.isFinite(row.overallScore)) ? includedRows : null;
  }

  function databaseTrendSnapshotRejectReason(snapshot) {
    const snapshotJson = snapshot?.snapshot_json ?? snapshot?.snapshotJson ?? snapshot;
    const rows = snapshotJson?.trendRows ?? snapshotJson?.rows;
    const snapshotVersion = snapshotJson?.trendRowsVersion ?? snapshotJson?.renderVersion ?? "";
    if (!Array.isArray(rows) || !rows.length) return "empty_snapshot";
    if (snapshotVersion !== trendCacheSchemaVersion) return "version_mismatch";
    if (snapshotJson?.renderReady !== true && snapshotJson?.uiReady !== true) return "not_render_ready";
    return "no_usable_rows";
  }

  async function readDatabaseTrendSnapshotRows(aircraft, { ifNoneMatch = null } = {}) {
    const aircraftId = selectedTrendAircraftDatabaseId(aircraft);
    if (!aircraftId) return { rows: null, reason: "no_aircraft" };
    const url = new URL(databaseTrendSnapshotEndpoint(aircraftId), window.location.href);
    url.searchParams.set("sensitivity", getDetectionSensitivity());
    // Conditional GET: when the caller already holds rows for a known ETag, send it so an
    // unchanged aircraft answers 304 and the server skips reading/serializing the multi-MB
    // snapshot body. cache:"no-store" keeps the browser's own HTTP cache out of the way; the
    // explicit If-None-Match header is still sent and the 304 is surfaced to us directly.
    const headers = {};
    if (ifNoneMatch) headers["if-none-match"] = ifNoneMatch;
    const response = await fetch(url.toString(), { cache: "no-store", headers });
    if (response.status === 304) {
      return { rows: null, reason: "not_modified", notModified: true };
    }
    if (response.status === 404) {
      let reason = "not_found";
      try {
        const body = await response.json();
        if (typeof body?.reason === "string") reason = body.reason;
      } catch (_) {}
      return { rows: null, reason };
    }
    if (!response.ok) throw new Error(`Trend snapshot API HTTP ${response.status}`);
    const payload = await response.json();
    const snapshotJson = payload.snapshot?.snapshot_json ?? payload.snapshot?.snapshotJson ?? payload.snapshot;
    const rows = databaseTrendSnapshotRows(payload.snapshot, aircraft);
    return {
      rows,
      reason: typeof payload.reason === "string"
        ? payload.reason
        : rows ? "ok" : databaseTrendSnapshotRejectReason(payload.snapshot),
      stale: Boolean(payload.stale),
      etag: response.headers.get("etag") ?? null,
      snapshotVersion: snapshotJson?.trendRowsVersion ?? snapshotJson?.renderVersion ?? "",
      expectedVersion: trendCacheSchemaVersion,
    };
  }

  async function saveDatabaseTrendSnapshotRows(aircraft, rows) {
    const aircraftId = selectedTrendAircraftDatabaseId(aircraft);
    if (!aircraftId || !Array.isArray(rows) || !rows.length) return null;
    if (appReadOnlyMode()) return null;
    const response = await fetch(databaseTrendSnapshotEndpoint(aircraftId), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ trendRows: rows }),
    });
    if (!response.ok) throw new Error(`Trend snapshot save API HTTP ${response.status}`);
    const payload = await response.json();
    const savedRows = databaseTrendSnapshotRows(payload.snapshot, aircraft);
    return savedRows ?? rows;
  }

  return {
    buildTrendRow,
    trendSelectedFlightIndex,
    trendAnchorIndex,
    trendWindowRows,
    scoreClassForValue,
    trendRenderSignature,
    trendDataRenderSignature,
    trendRowsIncludedByPreference,
    trendRowCompletenessScore,
    preferTrendRowCandidate,
    normalizeTrendRowsForDisplay,
    databaseTrendSnapshotRows,
    readDatabaseTrendSnapshotRows,
    saveDatabaseTrendSnapshotRows,
  };
}
